import {FormEvent, useId, useState} from "react";
import {Badge, Button, ButtonGroup, Card, CloseButton, Form, ListGroup, Modal, ProgressBar} from "react-bootstrap";
import {ProductsApi} from "../../../api/ProductsApi";
import {
    OperatingSystem,
    OperatingSystems,
    ProductFileViewModel,
    ProductVersionViewModel,
    ProductViewModel
} from "../../../api/models";
import {uploadToPresignedUrl} from "../../../api/upload";
import {common} from "../../../i18n";
import {ErrorAlert, fieldError} from "../../../components/ErrorAlert";
import {ConfirmModal} from "../../../components/ConfirmModal";
import {CopyButton} from "../../../components/CopyButton";
import {Field, FieldControl} from "../../../components/Field";
import {SubmitButton} from "../../../components/SubmitButton";
import {guessOperatingSystem, OperatingSystemLabel} from "../../../components/OperatingSystemLabel";
import {formatBytes, formatCalendarDate, fromDateInput, isValidVersion, toDateInput} from "../../../components/format";

// The file store's answer to an upload (src/api/upload.ts), which has no error code.
const versionMessages = {
    403: 'The upload link was refused or expired. Try again.'
};

// A file waiting to be uploaded, with the title and operating system it'll be listed under, and its SHA-256 if given.
interface PendingFile {
    file: File;
    title: string;
    operatingSystem: OperatingSystem;
    sha256: string;
    progress?: number;
    done?: boolean;
}

// Optional: blank, or 64 hex digits (any case; the API stores lower case).
const isValidSha256 = (value: string) => !value.trim() || /^[0-9a-f]{64}$/i.test(value.trim());
const sha256Error = 'A SHA-256 is 64 hexadecimal digits.';

const defaultTitle = (product: ProductViewModel, file: File, os: OperatingSystem) =>
    os === 'any' ? file.name.replace(/\.[^.]+$/, '') : `${product.name} for ${common.operatingSystems[os]}`;

// Adds the file's record, PUTs it straight to S3, then confirms the upload so it can be downloaded.
async function uploadFile(productId: string, versionId: string, pending: PendingFile, onProgress: (fraction: number) => void) {
    const {file, upload} = await ProductsApi.addFile(productId, versionId, {
        title: pending.title.trim(),
        operatingSystem: pending.operatingSystem,
        fileName: pending.file.name,
        sha256: pending.sha256.trim() || undefined
    });
    await uploadToPresignedUrl(upload, pending.file, onProgress);
    await ProductsApi.completeFile(productId, versionId, file.id);
}

// Uploads each pending file in turn, reporting progress, and leaves the finished ones marked done (so a retry skips them).
async function uploadAll(productId: string, versionId: string, files: PendingFile[], setFiles: (update: (files: PendingFile[]) => PendingFile[]) => void) {
    for (const [index, pending] of files.entries()) {
        if (pending.done) {
            continue;
        }
        const set = (changes: Partial<PendingFile>) => setFiles(rows => rows.map((x, i) => i === index ? { ...x, ...changes } : x));
        set({ progress: 0 });
        await uploadFile(productId, versionId, pending, progress => set({ progress }));
        set({ done: true, progress: undefined });
    }
}

// A product's versions and their files (one per operating system, say), uploaded straight to S3.
// The current version is the one the update check offers. Delisted versions are hidden unless asked for.
export function ProductVersions({product, onChanged}: { product: ProductViewModel, onChanged(): void }) {
    const [editing, setEditing] = useState<ProductVersionViewModel | 'new'>();
    const [deleting, setDeleting] = useState<ProductVersionViewModel>();
    const [addingTo, setAddingTo] = useState<ProductVersionViewModel>();
    const [editingFile, setEditingFile] = useState<{ version: ProductVersionViewModel, file: ProductFileViewModel }>();
    const [deletingFile, setDeletingFile] = useState<{ version: ProductVersionViewModel, file: ProductFileViewModel }>();
    const [showDelisted, setShowDelisted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const delistedCount = product.versions.filter(x => x.delisted).length;
    const shown = product.versions.filter(x => showDelisted || !x.delisted);

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(undefined);
        try {
            await action();
            onChanged();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const saved = () => {
        setEditing(undefined);
        setAddingTo(undefined);
        setEditingFile(undefined);
        onChanged();
    };

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex align-items-center">
                Versions
                {delistedCount > 0 && (
                    <Form.Check type="switch" id="versions-show-delisted" className="ms-auto mb-0 small" label={`Show delisted (${delistedCount})`}
                                checked={showDelisted} onChange={e => setShowDelisted(e.target.checked)}/>
                )}
                <Button size="sm" className={delistedCount > 0 ? 'ms-3' : 'ms-auto'} onClick={() => setEditing('new')}><i className="bi bi-plus-lg me-1"></i>New version</Button>
            </Card.Header>
            <Card.Body>
                <ErrorAlert error={error} messages={versionMessages} onClose={() => setError(undefined)}/>
                {product.versions.length === 0 && <p className="text-body-secondary mb-0">No versions yet.</p>}
                {product.versions.length > 0 && shown.length === 0 && <p className="text-body-secondary">Every version is delisted.</p>}
                {shown.map(version => {
                    const hasUploaded = version.files.some(x => x.uploaded);
                    return (
                        <div key={version.id} className={`border rounded mb-3${version.delisted ? ' opacity-75' : ''}`}>
                            <div className="d-flex flex-wrap align-items-center gap-2 p-2">
                                <strong>{version.version}</strong>
                                {version.isCurrent && <Badge bg="success">{version.earlyAccess ? 'Current Early Access' : 'Current GA'}</Badge>}
                                {version.earlyAccess && <Badge bg="info" text="dark" title="Only for Early Access Users with an Early Access license">Early Access Version</Badge>}
                                {version.delisted && <Badge bg="secondary" title="Hidden from the website, and only administrators can download it">Delisted</Badge>}
                                <span className="small text-body-secondary">Released {formatCalendarDate(version.releasedOn)}</span>
                                <ButtonGroup size="sm" className="ms-auto">
                                    <Button variant="outline-secondary" disabled={busy} onClick={() => setAddingTo(version)}>
                                        <i className="bi bi-upload me-1"></i>Add files
                                    </Button>
                                    {!version.isCurrent && !version.delisted && (
                                        <Button variant="outline-success" disabled={busy || !hasUploaded}
                                                title={!hasUploaded ? 'Upload a file first'
                                                    : version.earlyAccess ? 'Replaces the current Early Access Version' : 'Replaces the current GA version'}
                                                onClick={() => run(() => ProductsApi.makeCurrent(product.id, version.id))}>Make current</Button>
                                    )}
                                    <Button variant="outline-secondary" disabled={busy} onClick={() => setEditing(version)} aria-label="Edit">
                                        <i className="bi bi-pencil" aria-hidden="true"></i>
                                    </Button>
                                    {!version.isCurrent && (
                                        <Button variant="outline-secondary" disabled={busy}
                                                title={version.delisted ? 'Show it on the website again' : "Hide it from the website and stop people downloading it. Builds of it keep working and are offered updates."}
                                                onClick={() => run(() => ProductsApi.setDelisted(product.id, version.id, !version.delisted))}>
                                            {version.delisted ? 'List again' : 'Delist'}
                                        </Button>
                                    )}
                                    {!version.isCurrent && (
                                        <Button variant="outline-danger" disabled={busy} onClick={() => setDeleting(version)} aria-label="Delete">
                                            <i className="bi bi-trash" aria-hidden="true"></i>
                                        </Button>
                                    )}
                                </ButtonGroup>
                            </div>
                            <ListGroup variant="flush" className="border-top">
                                {version.files.length === 0 && <ListGroup.Item className="small text-body-secondary">No files</ListGroup.Item>}
                                {version.files.map(file => (
                                    <ListGroup.Item key={file.id} className="d-flex flex-wrap align-items-center gap-2 small">
                                        <span className="product-file-os"><OperatingSystemLabel os={file.operatingSystem}/></span>
                                        <span className="me-auto">
                                            {file.title}
                                            <span className="d-block text-body-secondary text-break">
                                                {file.fileName}{file.uploaded && ` · ${formatBytes(file.fileSize)}`}
                                            </span>
                                        </span>
                                        {!file.uploaded && (
                                            <>
                                                <Badge bg="warning" text="dark">Not uploaded</Badge>
                                                <Button size="sm" variant="outline-secondary" disabled={busy}
                                                        title="Check whether the upload finished"
                                                        onClick={() => run(() => ProductsApi.completeFile(product.id, version.id, file.id))}>Check</Button>
                                            </>
                                        )}
                                        <ButtonGroup size="sm">
                                            <CopyButton text={file.downloadUrl} label="Copy the download link" icon="bi-link-45deg" disabled={busy}/>
                                            <Button variant="outline-secondary" disabled={busy} onClick={() => setEditingFile({ version, file })} aria-label="Edit file">
                                                <i className="bi bi-pencil" aria-hidden="true"></i>
                                            </Button>
                                            <Button variant="outline-danger" disabled={busy} onClick={() => setDeletingFile({ version, file })} aria-label="Delete file">
                                                <i className="bi bi-trash" aria-hidden="true"></i>
                                            </Button>
                                        </ButtonGroup>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    );
                })}
                {product.versions.length > 0 && !product.currentVersion && (
                    <p className="small text-body-secondary mb-0">No GA version is current, so the update check finds nothing to offer released builds.</p>
                )}
            </Card.Body>
            {editing && (
                <VersionModal product={product} version={editing === 'new' ? undefined : editing}
                              onHide={() => setEditing(undefined)} onSaved={saved}/>
            )}
            {addingTo && <AddFilesModal product={product} version={addingTo} onHide={() => setAddingTo(undefined)} onSaved={saved}/>}
            {editingFile && <FileModal product={product} {...editingFile} onHide={() => setEditingFile(undefined)} onSaved={saved}/>}
            <ConfirmModal show={!!deleting} title={`Delete version ${deleting?.version ?? ''}?`} confirmLabel="Delete" busy={busy}
                          onConfirm={() => { const version = deleting!; setDeleting(undefined); void run(() => ProductsApi.removeVersion(product.id, version.id)); }}
                          onCancel={() => setDeleting(undefined)}>
                Its files are deleted too. People can no longer download them. Builds of a deleted version are no longer
                recognized: an Early Access build's license stops renewing. To withdraw it but keep its builds working, delist it instead.
            </ConfirmModal>
            <ConfirmModal show={!!deletingFile} title={`Delete ${deletingFile?.file.title ?? 'this file'}?`} confirmLabel="Delete" busy={busy}
                          onConfirm={() => {
                              const {version, file} = deletingFile!;
                              setDeletingFile(undefined);
                              void run(() => ProductsApi.removeFile(product.id, version.id, file.id));
                          }}
                          onCancel={() => setDeletingFile(undefined)}>
                People can no longer download {deletingFile?.file.fileName}, and its download link stops working.
            </ConfirmModal>
        </Card>
    );
}

// Rows for files to upload: each one's title and operating system, with its progress while uploading.
// required: at least one file still to upload.
function PendingFiles({product, files, setFiles, disabled, validated, required}: {
    product: ProductViewModel,
    files: PendingFile[],
    setFiles(update: (files: PendingFile[]) => PendingFile[]): void,
    disabled: boolean,
    validated: boolean,
    required?: boolean
}) {
    const [inputKey, setInputKey] = useState(0);

    const choose = (chosen: FileList | null) => {
        const added = Array.from(chosen ?? []).map(file => {
            const operatingSystem = guessOperatingSystem(file.name);
            return { file, operatingSystem, title: defaultTitle(product, file, operatingSystem), sha256: '' };
        });
        setFiles(rows => [...rows, ...added]);
        setInputKey(x => x + 1); // so the same file can be chosen again
    };

    const set = (index: number, changes: Partial<PendingFile>) => setFiles(rows => rows.map((x, i) => i === index ? { ...x, ...changes } : x));
    const noFiles = validated && required && !files.some(x => !x.done);

    return (
        <>
            {files.map((row, index) => {
                const titleMissing = validated && !row.title.trim();
                const errorId = `pending-file-${index}-error`;
                const sha256Invalid = validated && !isValidSha256(row.sha256);
                const sha256ErrorId = `pending-file-${index}-sha256-error`;
                return (
                    <div key={index} className="border rounded p-2 mb-2">
                        <div className="d-flex align-items-center gap-2 mb-2 small">
                            <i className="bi bi-file-earmark" aria-hidden="true"></i>
                            <span className="text-break me-auto">{row.file.name} <span className="text-body-secondary">{formatBytes(row.file.size)}</span></span>
                            {row.done
                                ? <i className="bi bi-check-circle-fill text-success" role="img" aria-label="Uploaded"></i>
                                : <CloseButton disabled={disabled} aria-label={`Remove ${row.file.name}`} onClick={() => setFiles(rows => rows.filter((_, i) => i !== index))}/>}
                        </div>
                        <div className="d-flex gap-2">
                            <Form.Control size="sm" placeholder="Title" aria-label={`Title for ${row.file.name}`} value={row.title} disabled={disabled || row.done}
                                          onChange={e => set(index, { title: e.target.value })} isInvalid={titleMissing}
                                          aria-invalid={titleMissing || undefined} aria-describedby={titleMissing ? errorId : undefined}/>
                            <Form.Select size="sm" aria-label={`Operating system for ${row.file.name}`} style={{ maxWidth: '9rem' }} value={row.operatingSystem} disabled={disabled || row.done}
                                         onChange={e => set(index, { operatingSystem: e.target.value as OperatingSystem })}>
                                {OperatingSystems.map(os => <option key={os} value={os}>{common.operatingSystems[os]}</option>)}
                            </Form.Select>
                        </div>
                        {titleMissing && <div id={errorId} className="invalid-feedback d-block">Enter a title.</div>}
                        <Form.Control size="sm" className="mt-2 font-monospace" placeholder="SHA-256 (optional)" aria-label={`SHA-256 for ${row.file.name}`}
                                      value={row.sha256} disabled={disabled || row.done} spellCheck={false} autoComplete="off"
                                      onChange={e => set(index, { sha256: e.target.value })} isInvalid={sha256Invalid}
                                      aria-invalid={sha256Invalid || undefined} aria-describedby={sha256Invalid ? sha256ErrorId : undefined}/>
                        {sha256Invalid && <div id={sha256ErrorId} className="invalid-feedback d-block">{sha256Error}</div>}
                        {row.progress !== undefined && <ProgressBar className="mt-2" now={row.progress * 100} label={`${Math.round(row.progress * 100)}%`}/>}
                    </div>
                );
            })}
            <Form.Control key={inputKey} type="file" multiple disabled={disabled} aria-label="Choose files" isInvalid={noFiles}
                          aria-invalid={noFiles || undefined} aria-describedby={noFiles ? 'pending-files-error' : undefined}
                          onChange={e => choose((e.target as HTMLInputElement).files)}/>
            {noFiles && <div id="pending-files-error" className="invalid-feedback d-block">Choose at least one file to upload.</div>}
        </>
    );
}

// Adds a version (optionally uploading its files and making it current), or edits one's details.
function VersionModal({product, version, onHide, onSaved}: {
    product: ProductViewModel,
    version?: ProductVersionViewModel,
    onHide(): void,
    onSaved(): void
}) {
    const titleId = useId();
    const [number, setNumber] = useState(version?.version ?? '');
    const [releasedOn, setReleasedOn] = useState(toDateInput(version?.releasedOn));
    const [notes, setNotes] = useState(version?.releaseNotes ?? '');
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [created, setCreated] = useState<ProductVersionViewModel>();
    const [makeCurrent, setMakeCurrent] = useState(true);
    const [earlyAccess, setEarlyAccess] = useState(version?.earlyAccess ?? false);
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState('');
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!isValidVersion(number) || !releasedOn || files.some(x => !x.title.trim() || !isValidSha256(x.sha256))) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const model = { version: number.trim(), releaseNotes: notes.trim(), releasedOn: fromDateInput(releasedOn), earlyAccess };
            if (version) {
                await ProductsApi.updateVersion(product.id, version.id, model);
            } else {
                // After a failed upload, a retry carries on with the version already created.
                let target = created;
                if (!target) {
                    setStep('Creating the version…');
                    target = await ProductsApi.addVersion(product.id, model);
                    setCreated(target);
                }
                if (files.length) {
                    setStep('Uploading…');
                    await uploadAll(product.id, target.id, files, setFiles);
                    if (makeCurrent) {
                        setStep('Making it current…');
                        await ProductsApi.makeCurrent(product.id, target.id);
                    }
                }
            }
            onSaved();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setStep('');
        }
    };

    // Once created, closing still refreshes the list, so the new version shows.
    const close = created ? onSaved : onHide;

    return (
        <Modal aria-labelledby={titleId} show onHide={busy ? () => undefined : close} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton={!busy}><Modal.Title id={titleId}>{version ? `Version ${version.version}` : 'New version'}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} messages={versionMessages} shownFields={['version', 'releasedOn']}/>
                    <div className="d-flex gap-3">
                        <Field className="mb-3 flex-grow-1" controlId="version-number" label="Version"
                               error={(validated && !isValidVersion(number) && 'Like 1.2.0 or 1.2.0-ea.1.') || fieldError(error, 'version')}>
                            <FieldControl placeholder="1.2.0" value={number} disabled={!!created} onChange={e => setNumber(e.target.value)}/>
                        </Field>
                        <Field className="mb-3" controlId="version-released" label="Released"
                               error={(validated && !releasedOn && 'Choose the release date.') || fieldError(error, 'releasedOn')}>
                            <FieldControl type="date" value={releasedOn} disabled={!!created} onChange={e => setReleasedOn(e.target.value)}/>
                        </Field>
                    </div>
                    <Form.Group className="mb-3" controlId="version-notes">
                        <Form.Label>Release notes</Form.Label>
                        <Form.Control as="textarea" rows={5} value={notes} disabled={!!created} onChange={e => setNotes(e.target.value)}/>
                    </Form.Group>
                    {/* Switching it would leave its own kind without a current version. */}
                    <Form.Check type="switch" id="version-early-access" className="mb-1" label="Early Access Version"
                                checked={earlyAccess} disabled={!!created || !!version?.isCurrent} aria-describedby="version-early-access-help"
                                onChange={e => setEarlyAccess(e.target.checked)}/>
                    <Form.Text id="version-early-access-help" className="d-block mb-3">
                        {version?.isCurrent
                            ? "A current version can't switch between Early Access and GA. Make another version current first."
                            : 'Only Early Access Users with an Early Access license see it, download it and can activate it. Early Access Versions have their own current version, offered to Early Access builds by the update check.'}
                    </Form.Text>
                    {!version && (
                        <>
                            <Form.Label>Files <span className="text-body-secondary">(optional, can be added later)</span></Form.Label>
                            <div className="mb-3">
                                <PendingFiles product={product} files={files} setFiles={setFiles} disabled={busy} validated={validated}/>
                            </div>
                            <Form.Check type="switch" id="version-current" label={earlyAccess ? 'Make it the current Early Access Version' : 'Make it the current GA version'}
                                        disabled={!files.length} checked={!!files.length && makeCurrent} onChange={e => setMakeCurrent(e.target.checked)}/>
                        </>
                    )}
                    {step && <p className="small text-body-secondary mt-3 mb-0">{step}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close} disabled={busy}>{created ? 'Close' : 'Cancel'}</Button>
                    <SubmitButton busy={busy}>{version ? 'Save' : created ? 'Retry' : 'Add version'}</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

// Uploads more files to an existing version.
function AddFilesModal({product, version, onHide, onSaved}: {
    product: ProductViewModel,
    version: ProductVersionViewModel,
    onHide(): void,
    onSaved(): void
}) {
    const titleId = useId();
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!files.some(x => !x.done) || files.some(x => !x.title.trim() || !isValidSha256(x.sha256))) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await uploadAll(product.id, version.id, files, setFiles);
            onSaved();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    // Some may have uploaded before a failure, so closing refreshes the list.
    const close = files.some(x => x.done) ? onSaved : onHide;

    return (
        <Modal aria-labelledby={titleId} show onHide={busy ? () => undefined : close} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton={!busy}><Modal.Title id={titleId}>Add files to {version.version}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} messages={versionMessages}/>
                    <PendingFiles product={product} files={files} setFiles={setFiles} disabled={busy} validated={validated} required/>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Upload</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

// Edits a file's title, operating system and SHA-256.
function FileModal({product, version, file, onHide, onSaved}: {
    product: ProductViewModel,
    version: ProductVersionViewModel,
    file: ProductFileViewModel,
    onHide(): void,
    onSaved(): void
}) {
    const titleId = useId();
    const [title, setTitle] = useState(file.title);
    const [operatingSystem, setOperatingSystem] = useState(file.operatingSystem);
    const [sha256, setSha256] = useState(file.sha256 ?? '');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!title.trim() || !isValidSha256(sha256)) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await ProductsApi.updateFile(product.id, version.id, file.id, { title: title.trim(), operatingSystem, sha256: sha256.trim() });
            onSaved();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={busy ? () => undefined : onHide} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton={!busy}><Modal.Title id={titleId}>{file.fileName}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} shownFields={['sha256']}/>
                    <Field className="mb-3" controlId="file-title" label="Title" error={validated && !title.trim() && 'Enter a title.'}>
                        <FieldControl value={title} onChange={e => setTitle(e.target.value)}/>
                    </Field>
                    <Form.Group className="mb-3" controlId="file-os">
                        <Form.Label>Operating system</Form.Label>
                        <Form.Select value={operatingSystem} onChange={e => setOperatingSystem(e.target.value as OperatingSystem)}>
                            {OperatingSystems.map(os => <option key={os} value={os}>{common.operatingSystems[os]}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <Field className="mb-3" controlId="file-sha256" label="SHA-256" help="Optional. Leave blank to remove it."
                           error={(validated && !isValidSha256(sha256) && sha256Error) || fieldError(error, 'sha256')}>
                        <FieldControl className="font-monospace" value={sha256} spellCheck={false} autoComplete="off" onChange={e => setSha256(e.target.value)}/>
                    </Field>
                    <Field controlId="file-link" label="Download link" help="For license holders. To replace the file, add the new one and delete this one.">
                        <FieldControl readOnly value={file.downloadUrl} onFocus={e => e.target.select()}/>
                    </Field>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Save</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
